import React, { useMemo } from 'react';
import { Box, Text, useStdout } from 'ink';
import Spinner from 'ink-spinner';
import { RequestLog } from '../components/RequestLog';
import { NavigationMenu } from '../../components/NavigationMenu';
import type { MenuItem } from '../../components/NavigationMenu';
import type { EditorSession } from '../../../api';
import type {
  LocalModel,
  Provider,
  ProviderSetupStatus,
  ComfyWorkflowParameterSchema,
} from '../../../providers/types';
import type { RequestLogEntry } from '../../types';

function getWorkflowCount(model: LocalModel): number | null {
  const param = model.parameters?.find((p) => p.type === 'comfyWorkflow');
  if (!param) return null;
  return (param as ComfyWorkflowParameterSchema).comfyWorkflowOptions
    .availableWorkflows.length;
}

function getCapabilityLabel(capability: string): {
  label: string;
  color: string;
} {
  switch (capability) {
    case 'text':
      return { label: 'Text Generation', color: 'gray' };
    case 'image':
      return { label: 'Image Generation', color: 'gray' };
    case 'video':
      return { label: 'Video Generation', color: 'gray' };
    default:
      return { label: capability, color: 'gray' };
  }
}

interface ProviderWithStatus {
  provider: Provider;
  status: ProviderSetupStatus;
}

interface DashboardPageProps {
  requests: RequestLogEntry[];
  models: LocalModel[];
  modelWarnings?: LocalModel[];
  providers: ProviderWithStatus[];
  providersLoading?: boolean;
  syncedNames: Set<string>;
  modelsLoading?: boolean;
  syncStatus?: 'idle' | 'syncing' | 'synced';
  editorSessions: EditorSession[];
  editorsLoading: boolean;
  onNavigate: (id: string) => void;
}

export function DashboardPage({
  requests,
  models,
  modelWarnings = [],
  providers,
  providersLoading,
  syncedNames,
  modelsLoading,
  syncStatus = 'idle',
  editorSessions,
  editorsLoading,
  onNavigate,
}: DashboardPageProps) {
  const { stdout } = useStdout();

  const installedProviders = providers.filter(({ status }) => status.installed);

  const provNameWidth = Math.max(
    ...installedProviders.map((p) => p.provider.displayName.length),
    8,
  );
  const provStatusWidth = 'Local Server Running'.length;

  const allModelNames = new Set(models.map((m) => m.name));
  const unavailableSynced = [...syncedNames].filter(
    (name) => !allModelNames.has(name),
  );

  const syncDescription =
    syncStatus === 'syncing'
      ? 'Syncing...'
      : syncStatus === 'synced'
        ? '\u2713 Synced'
        : 'Re-detect providers and sync models to MindStudio';

  const menuItems = useMemo((): MenuItem[] => {
    return [
      {
        id: 'interfaces',
        label: 'Connect to Agent',
        description:
          'Connect your local editor to a MindStudio interface or script',
      },
      {
        id: 'refresh',
        label: 'Sync Models',
        description: syncDescription,
      },
      {
        id: 'setup',
        label: 'Manage Providers',
        description: 'Manage local AI providers',
      },
      {
        id: 'auth',
        label: 'Re-authenticate',
        description: 'Re-authenticate with MindStudio',
      },
      {
        id: 'quit',
        label: 'Exit',
        description: 'Quit the application',
      },
    ];
  }, [syncDescription]);

  // Compute maxVisible for request log based on terminal height
  const termHeight = (stdout?.rows ?? 24) - 4; // matches App's height calculation
  const compactHeader =
    (stdout?.rows ?? 24) <= 45 || (stdout?.columns ?? 80) <= 90;

  // Header: compact (no logo, version on title line) = border(2) + padding(2) + 3 text lines = 7, full = border(2) + padding(2) + logo(~10 lines) = 14
  const headerLines = compactHeader ? 7 : 14;

  // Providers section: marginTop(1) + title(1) + content gap(1) + content
  const providerContentLines = providersLoading
    ? 1
    : installedProviders.length === 0
      ? 2
      : installedProviders.length;
  const providersLines = 3 + providerContentLines;

  // Open Editors section: marginTop(1) + title(1) + content gap(1) + content
  const editorsContentLines = editorsLoading
    ? 1
    : editorSessions.length === 0
      ? 1
      : editorSessions.length;
  const editorsLines = 3 + editorsContentLines;

  // Models section: marginTop(1) + title(1) + content gap(1) + content
  const modelContentLines = modelsLoading
    ? 1
    : models.length === 0 &&
        unavailableSynced.length === 0 &&
        modelWarnings.length === 0
      ? 2
      : models.length +
        modelWarnings.length +
        (unavailableSynced.length > 0 ? 1 + unavailableSynced.length : 0);
  const modelsLines = 3 + modelContentLines;

  // Request log overhead: marginTop(1) + title(1) + content gap(1)
  const requestLogOverhead = 3;

  // Menu: compact = border-top(1) + items(1) + hint(1) = 3, full = border-top(1) + marginTop(1) + title(1) + items + hint(1) + marginTop(1) + marginBottom(1)
  const compactMenu = termHeight + 4 < 40; // stdout rows < 40
  const menuLines = compactMenu ? 2 : menuItems.length + 6;

  const usedLines =
    headerLines +
    providersLines +
    editorsLines +
    modelsLines +
    requestLogOverhead +
    menuLines;
  const maxVisible = Math.max(3, termHeight - usedLines);

  return (
    <Box flexDirection="column" flexGrow={1}>
      {/* Open Editors */}
      <Box flexDirection="column" paddingX={1} marginTop={1}>
        <Text bold color="white" underline>
          Open Agents
        </Text>

        {editorsLoading ? (
          <Box marginTop={1}>
            <Text color="cyan">
              <Spinner type="dots" />
            </Text>
            <Text> Loading editor sessions...</Text>
          </Box>
        ) : editorSessions.length === 0 ? (
          <Box marginTop={1}>
            <Text color="gray">
              Agents you are editing in the MindStudio IDE will appear here.
            </Text>
          </Box>
        ) : (
          <Box flexDirection="column" marginTop={1}>
            {editorSessions.map((session) => (
              <Box key={session.appId}>
                <Text color="white">{session.appName}</Text>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Providers */}
      <Box flexDirection="column" paddingX={1} marginTop={1}>
        <Text bold color="white" underline>
          AI Model Providers
        </Text>

        {providersLoading ? (
          <Box marginTop={1}>
            <Text color="cyan">
              <Spinner type="dots" />
            </Text>
            <Text> Detecting providers...</Text>
          </Box>
        ) : installedProviders.length === 0 ? (
          <Box marginTop={1} flexDirection="column">
            <Text color="yellow">No providers installed.</Text>
            <Text color="gray">
              Use "Manage Providers" below to install one.
            </Text>
          </Box>
        ) : (
          <Box flexDirection="column" marginTop={1}>
            {installedProviders.map(({ provider, status }) => {
              const url = provider.baseUrl;
              const statusColor = status.running ? 'green' : 'yellow';
              const statusText = status.running
                ? 'Local Server Running'
                : 'Installed (not running)';

              return (
                <Box key={provider.name}>
                  <Text color="white">
                    {provider.displayName.padEnd(provNameWidth + 2)}
                  </Text>
                  <Text color={statusColor}>
                    {statusText.padEnd(provStatusWidth + 2)}
                  </Text>
                  {status.running && <Text color="gray">{url}</Text>}
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      {/* Models */}
      <Box flexDirection="column" paddingX={1} marginTop={1}>
        <Text bold color="white" underline>
          Local Models
        </Text>

        {modelsLoading ? (
          <Box marginTop={1}>
            <Text color="cyan">
              <Spinner type="dots" />
            </Text>
            <Text> Discovering models...</Text>
          </Box>
        ) : models.length === 0 &&
          unavailableSynced.length === 0 &&
          modelWarnings.length === 0 ? (
          <Box marginTop={1} flexDirection="column">
            <Text color="yellow">No models found.</Text>
            <Text color="gray">
              Download models using your provider (e.g., ollama pull llama3.2)
            </Text>
          </Box>
        ) : (
          <Box flexDirection="column" marginTop={1}>
            {models.map((model) => {
              const cap = getCapabilityLabel(model.capability);
              const isSynced = syncedNames.has(model.name);
              const displayProvider =
                providers.find((p) => p.provider.name === model.provider)
                  ?.provider.displayName ?? model.provider;
              const workflowCount = getWorkflowCount(model);
              const workflowSuffix =
                workflowCount !== null
                  ? ` (${workflowCount} workflow${workflowCount !== 1 ? 's' : ''}, ${isSynced ? workflowCount : 0} synced)`
                  : '';
              return (
                <Box key={`${model.provider}:${model.name}`}>
                  <Text color={isSynced ? 'green' : 'gray'}>
                    {isSynced ? '\u25CF' : '\u25CB'}
                  </Text>
                  <Text color="white">{` ${model.name}`}</Text>
                  {workflowSuffix && <Text color="gray">{workflowSuffix}</Text>}
                  <Text color="gray">{' - '}</Text>
                  <Text color="gray">{displayProvider}</Text>
                  <Text color="gray">{' - '}</Text>
                  <Text color={cap.color}>{cap.label}</Text>
                </Box>
              );
            })}
            {modelWarnings.map((warning) => {
              const displayProvider =
                providers.find((p) => p.provider.name === warning.provider)
                  ?.provider.displayName ?? warning.provider;
              return (
                <Box key={`${warning.provider}:${warning.name}`}>
                  <Text color="gray">{'\u25CB'}</Text>
                  <Text color="white">{` ${warning.name}`}</Text>
                  <Text color="gray">{' - '}</Text>
                  <Text color="gray">{displayProvider}</Text>
                  <Text color="gray">{' - '}</Text>
                  <Text color="yellow">{warning.statusHint}</Text>
                </Box>
              );
            })}

            {unavailableSynced.length > 0 && (
              <Box flexDirection="column" marginTop={models.length > 0 ? 1 : 0}>
                <Text color="gray">Synced but provider not running:</Text>
                {unavailableSynced.map((name) => (
                  <Box key={name}>
                    <Text color="gray">{'\u25CB'}</Text>
                    <Text color="gray">{` ${name}`}</Text>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Request log */}
      <RequestLog
        requests={requests}
        maxVisible={maxVisible}
        hasModels={models.length > 0}
      />

      {/* Bottom: Navigation menu pane */}
      <NavigationMenu items={menuItems} onSelect={onNavigate} />
    </Box>
  );
}
